import { useEffect, useState, useRef, useMemo } from "react";
import { X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import VoiceAgentAvatar from "@/components/VoiceAgent/VoiceAgentAvatar";
import { useVoiceAgentConversation } from "@/hooks/voice/useVoiceAgentConversation";
import { useSaveManualTest } from "@/hooks/agent/useBrandIntegrity";
import { TranscriptStorageService } from "@/services/conversation/TranscriptStorageService";
import { cn } from "@/lib/utils";
import { Agent } from "@/types/agent/agent";
import { ConversationTurn } from "@/types/agent/brand-integrity";
import { DEFAULT_BRANDING } from "@/types/ui/branding";

interface TranscriptMessage {
  message: string;
  isUser: boolean;
  timestamp: Date;
}

interface BrandQuickTestModalProps {
  agents: Agent[];
  isOpen: boolean;
  onClose: () => void;
  onTestSaved?: () => void;
  preSelectedAgentId?: string | null;
}

export const BrandQuickTestModal = ({
  agents,
  isOpen,
  onClose,
  onTestSaved,
  preSelectedAgentId,
}: BrandQuickTestModalProps) => {
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(
    preSelectedAgentId || agents[0]?.id || null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const conversationStartTimeRef = useRef<number | null>(null);
  const wasConnectedRef = useRef<boolean>(false);
  const lastConversationIdRef = useRef<string | null>(null);
  const isManualEndRef = useRef<boolean>(false);

  const selectedAgent = agents.find((a) => a.id === selectedAgentId);
  const elevenlabsId = selectedAgent?.elevenlabs_id;

  const { mutateAsync: saveManualTest } = useSaveManualTest();

  // Memoize overrides to prevent recreating VoiceAgentService on every render
  const conversationOverrides = useMemo(() => ({
    conversation: {
      max_duration_seconds: 1200, // 20 minutes
    },
  }), []);

  const voiceConversation = useVoiceAgentConversation(elevenlabsId, {
    agentData: selectedAgent,
    welcomeMessage: "Ready to test",
    conversationOverrides,
    isQuickTest: true,
  });

  const brand = selectedAgent?.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const secondaryColor = selectedAgent?.branding?.secondaryColor;
  const gradientEnabled = selectedAgent?.branding?.gradientEnabled;
  const gradientAngle = selectedAgent?.branding?.gradientAngle || 135;

  // Update selected agent when preSelectedAgentId changes
  useEffect(() => {
    if (isOpen && preSelectedAgentId) {
      setSelectedAgentId(preSelectedAgentId);
    }
  }, [isOpen, preSelectedAgentId]);

  // Setup message listening and transcript storage
  useEffect(() => {
    const service = voiceConversation.getService();
    if (service) {
      service.onMessage((message: string, isUser: boolean) => {
        const newMessage: TranscriptMessage = { message, isUser, timestamp: new Date() };
        setMessages((prev) => [...prev, newMessage]);

        // Store transcript in localStorage if we have a conversation ID
        const conversationId = voiceConversation.getConversationId();
        if (conversationId) {
          // Track the conversation ID for auto-save on unexpected disconnect
          lastConversationIdRef.current = conversationId;
          TranscriptStorageService.addMessage(
            conversationId,
            newMessage,
            conversationStartTimeRef.current || undefined
          );
        }
      });
    }
  }, [voiceConversation]);

  // Track connection state changes for auto-save on unexpected disconnect
  useEffect(() => {
    const previouslyConnected = wasConnectedRef.current;
    const currentlyConnected = voiceConversation.connected;

    // Update the ref for next comparison
    wasConnectedRef.current = currentlyConnected;

    // Only trigger auto-save when going from connected -> disconnected
    // and it wasn't a manual end (user clicking save button)
    if (previouslyConnected && !currentlyConnected && !isManualEndRef.current) {
      const conversationId = lastConversationIdRef.current;
      const storedTranscript = conversationId
        ? TranscriptStorageService.getStoredTranscript(conversationId)
        : null;

      if (conversationId && storedTranscript && storedTranscript.messages.length > 0 && selectedAgentId) {
        console.log("Auto-saving conversation after unexpected disconnect...");
        setIsSaving(true);

        const transcript: ConversationTurn[] = storedTranscript.messages.map((msg) => ({
          role: msg.isUser ? "user" : "agent",
          message: msg.message,
          time_in_call_secs: msg.timeInCallSecs,
        }));

        const durationSeconds = conversationStartTimeRef.current
          ? Math.floor((Date.now() - conversationStartTimeRef.current) / 1000)
          : undefined;

        saveManualTest({
          agentId: selectedAgentId,
          transcript,
          elevenlabsConversationId: conversationId,
          durationSeconds,
        })
          .then(() => {
            setSaveSuccess(true);
            toast.success("Conversation auto-saved after disconnect");
            onTestSaved?.();
            TranscriptStorageService.removeTranscript(conversationId);
            setTimeout(() => setSaveSuccess(false), 2000);
          })
          .catch((error) => {
            console.error("Failed to auto-save conversation:", error);
            toast.error("Conversation ended but failed to save");
          })
          .finally(() => {
            setIsSaving(false);
          });
      }
    }

    // Reset manual end flag when disconnected
    if (!currentlyConnected) {
      isManualEndRef.current = false;
    }
  }, [voiceConversation.connected, selectedAgentId, saveManualTest, onTestSaved]);

  // Cleanup on unmount or modal close
  useEffect(() => {
    if (!isOpen && voiceConversation.connected) {
      voiceConversation.endConversation();
    }
  }, [isOpen]);

  const handleClose = async () => {
    if (voiceConversation.connected) {
      await voiceConversation.endConversation();
    }
    setSaveSuccess(false);
    setIsSaving(false);
    onClose();
  };

  const saveConversation = async () => {
    if (!selectedAgentId) return;

    // Mark as manual end to prevent auto-save from triggering
    isManualEndRef.current = true;

    // IMPORTANT: Get the conversation ID and transcript BEFORE ending the conversation
    // because endConversation() may clean up the transcript storage
    const conversationId = voiceConversation.getConversationId();
    const storedTranscript = conversationId
      ? TranscriptStorageService.getStoredTranscript(conversationId)
      : null;

    // Now end the conversation
    await voiceConversation.endConversation();

    if (!conversationId || !storedTranscript || storedTranscript.messages.length === 0) {
      toast.error("No conversation to save");
      return;
    }

    setIsSaving(true);

    try {
      // Convert to the format expected by the service
      const transcript: ConversationTurn[] = storedTranscript.messages.map((msg) => ({
        role: msg.isUser ? "user" : "agent",
        message: msg.message,
        time_in_call_secs: msg.timeInCallSecs,
      }));

      // Calculate duration
      const durationSeconds = conversationStartTimeRef.current
        ? Math.floor((Date.now() - conversationStartTimeRef.current) / 1000)
        : undefined;

      await saveManualTest({
        agentId: selectedAgentId,
        transcript,
        elevenlabsConversationId: conversationId,
        durationSeconds,
      });

      setSaveSuccess(true);
      toast.success("Manual test saved to Genie Trust Center");
      onTestSaved?.();

      // Clean up the transcript from storage
      TranscriptStorageService.removeTranscript(conversationId);

      // Reset after brief delay
      setTimeout(() => {
        setSaveSuccess(false);
        handleClose();
      }, 1500);
    } catch (error) {
      console.error("Failed to save manual test:", error);
      toast.error("Failed to save test conversation");
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartConversation = async () => {
    // Track when conversation started and clear previous messages
    conversationStartTimeRef.current = Date.now();
    setMessages([]);
    await voiceConversation.startConversation();
  };

  const handleAgentChange = (agentId: string) => {
    if (voiceConversation.connected) {
      toast.error("Please end the current conversation first");
      return;
    }
    setSelectedAgentId(agentId);
  };

  // Match dimensions from GenieQuickTestModal for circular avatar
  // Original: 620 height, 320 avatar. We add ~80px for the agent selector
  const MODAL_WIDTH = 480;
  const MODAL_HEIGHT = 700;
  const AVATAR_SIZE = 320;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogPortal>
        <DialogOverlay className="bg-black/40 backdrop-blur-sm" />
        <DialogContent
          hideCloseButton
          className="p-0 border-0 overflow-hidden"
          style={{
            width: MODAL_WIDTH,
            height: MODAL_HEIGHT,
            borderRadius: "24px",
            maxWidth: "92vw",
            maxHeight: "90vh",
          }}
        >
          <DialogTitle className="sr-only">Manual Test</DialogTitle>
          <DialogDescription className="sr-only">
            Manually test your genie and save the conversation for review.
          </DialogDescription>

          {/* Gradient border wrapper */}
          <div
            className="absolute inset-0"
            style={{
              background:
                gradientEnabled && secondaryColor
                  ? `linear-gradient(${gradientAngle}deg, ${brand}, ${secondaryColor})`
                  : brand,
              padding: "2px",
              borderRadius: "24px",
            }}
          >
            <div className="w-full h-full bg-white rounded-[22px] relative flex flex-col">
              {/* Close button */}
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 z-10 transition-colors rounded-full p-1 hover:bg-gray-100"
                onClick={handleClose}
                aria-label="Close"
                disabled={isSaving}
              >
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>

              {/* Header */}
              <div className="pt-8 pb-4 px-6 text-center">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Manual Test</h2>
                <p className="text-sm text-gray-500">Start your manual simulation</p>
              </div>

              {/* Agent Selector */}
              <div className="px-6 pb-4">
                <select
                  value={selectedAgentId || ""}
                  onChange={(e) => handleAgentChange(e.target.value)}
                  disabled={voiceConversation.connected || isSaving}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">-- Select a genie --</option>
                  {agents
                    .filter((agent) => agent.elevenlabs_id)
                    .map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                </select>
                {selectedAgent && !elevenlabsId && (
                  <p className="text-sm text-red-600 mt-2">
                    This genie doesn't have an ElevenLabs agent configured.
                  </p>
                )}
              </div>

              {/* Avatar Section */}
              <div className="flex-1 flex items-center justify-center px-6">
                {selectedAgent && elevenlabsId ? (
                  <VoiceAgentAvatar
                    brandColor={brand}
                    size={AVATAR_SIZE}
                    customBranding={selectedAgent.branding}
                    onClick={voiceConversation.connected ? saveConversation : handleStartConversation}
                    speaking={voiceConversation.speaking}
                    listening={voiceConversation.listening}
                    isConnecting={voiceConversation.isConnecting}
                    connected={voiceConversation.connected}
                  />
                ) : (
                  <div
                    className="rounded-full bg-gray-100 flex items-center justify-center"
                    style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
                  >
                    <span className="text-gray-400 text-sm">Select a genie to test</span>
                  </div>
                )}
              </div>

              {/* Status message */}
              <div className="pb-6 px-6 text-center">
                {isSaving ? (
                  <>
                    <p className="text-sm font-medium text-genie-primary mb-1 flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving conversation...
                    </p>
                    <p className="text-xs text-gray-400">Please wait</p>
                  </>
                ) : saveSuccess ? (
                  <>
                    <p className="text-sm font-medium text-green-600 mb-1 flex items-center justify-center gap-2">
                      <Check className="h-4 w-4" />
                      Saved successfully!
                    </p>
                    <p className="text-xs text-gray-400">View in Genie Trust Center tests</p>
                  </>
                ) : selectedAgent && elevenlabsId ? (
                  <>
                    <p
                      className="text-sm font-medium mb-1"
                      style={{ color: voiceConversation.connected ? brand : "#6B7280" }}
                    >
                      {voiceConversation.getStatusMessage()}
                    </p>
                    <p className="text-xs text-gray-400">
                      {voiceConversation.connected
                        ? "Conversation will be saved when ended"
                        : "Click avatar to begin"}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-500 mb-1">No genie selected</p>
                    <p className="text-xs text-gray-400">Select a genie above to start testing</p>
                  </>
                )}
              </div>

              {/* Action button */}
              <div className="p-6 pt-0 flex justify-center">
                <button
                  onClick={voiceConversation.connected ? saveConversation : handleStartConversation}
                  disabled={
                    voiceConversation.isConnecting ||
                    !selectedAgent ||
                    !elevenlabsId ||
                    isSaving ||
                    saveSuccess
                  }
                  className={cn(
                    "font-semibold shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed",
                    voiceConversation.connected
                      ? "px-6 py-2.5 rounded-full text-sm text-white hover:shadow-lg hover:scale-[1.02]"
                      : "w-full py-3.5 rounded-xl text-base text-white hover:shadow-xl transform hover:scale-[1.02]"
                  )}
                  style={{
                    background:
                      gradientEnabled && secondaryColor
                        ? `linear-gradient(${gradientAngle}deg, ${brand}, ${secondaryColor})`
                        : brand,
                    opacity: voiceConversation.connected ? 0.9 : 1,
                  }}
                >
                  {voiceConversation.isConnecting
                    ? "Connecting..."
                    : voiceConversation.connected
                    ? "End & Save Conversation"
                    : !selectedAgent || !elevenlabsId
                    ? "Select a Genie"
                    : "Start Conversation"}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
